import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Session } from "@supabase/supabase-js";
import Dashboard from "./Dashboard";
import { assertAdminAccess } from "./lib/api";
import { supabase } from "./lib/supabase";

type Gate = "loading" | "login" | "denied" | "set-password" | "ready";

function recoveryRedirectTo(): string {
  return `${window.location.origin}/`;
}

function urlLooksLikeRecovery(): boolean {
  const hash = window.location.hash;
  return (
    hash.includes("type=recovery") ||
    new URLSearchParams(window.location.search).get("type") === "recovery"
  );
}

export default function App() {
  const [gate, setGate] = useState<Gate>("loading");
  const [session, setSession] = useState<Session | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const pendingPasswordReset = useRef(urlLooksLikeRecovery());

  useEffect(() => {
    let cancelled = false;

    async function enterAuthenticated(s: Session) {
      const ok = await assertAdminAccess();
      if (cancelled) return;
      if (!ok) {
        pendingPasswordReset.current = false;
        setSession(null);
        setGate("denied");
        return;
      }
      setSession(s);
      setGate(pendingPasswordReset.current ? "set-password" : "ready");
    }

    async function bootstrap(s: Session | null) {
      if (!s) {
        if (!cancelled) {
          pendingPasswordReset.current = false;
          setSession(null);
          setGate("login");
        }
        return;
      }
      await enterAuthenticated(s);
    }

    supabase.auth.getSession().then(({ data }) => {
      if (urlLooksLikeRecovery()) pendingPasswordReset.current = true;
      void bootstrap(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY") {
        pendingPasswordReset.current = true;
        void bootstrap(s);
        return;
      }
      if (event === "SIGNED_OUT") {
        void bootstrap(null);
        return;
      }
      if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        void bootstrap(s);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function onLogin(e: FormEvent) {
    e.preventDefault();
    setLoginError(null);
    setResetSent(false);
    setSigningIn(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) setLoginError(error.message);
    } finally {
      setSigningIn(false);
    }
  }

  async function onForgotPassword() {
    setLoginError(null);
    setResetSent(false);
    const target = email.trim();
    if (!target) {
      setLoginError("Enter your email first, then request a reset link.");
      return;
    }
    setSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(target, {
        redirectTo: recoveryRedirectTo(),
      });
      if (error) {
        setLoginError(error.message);
        return;
      }
      setResetSent(true);
    } finally {
      setSendingReset(false);
    }
  }

  if (gate === "loading") {
    return <div className="loading">Checking session…</div>;
  }

  if (gate === "set-password" && session) {
    return (
      <SetPasswordForm
        email={session.user.email ?? ""}
        onDone={() => {
          pendingPasswordReset.current = false;
          setGate("ready");
        }}
      />
    );
  }

  if (gate === "login" || gate === "denied") {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <h1>
            Meatmatics <span style={{ color: "var(--accent)" }}>Admin</span>
          </h1>
          <p className="muted" style={{ marginTop: 0 }}>
            Restricted access. Sign in with an allowlisted account.
          </p>
          {gate === "denied" && (
            <p className="error">
              That account is signed in but not on the admin allowlist.
            </p>
          )}
          <form onSubmit={onLogin}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {loginError && <p className="error">{loginError}</p>}
            {resetSent && (
              <p className="ok">
                Reset link sent — check your inbox, then set a new password on the
                page that opens.
              </p>
            )}
            <div className="row">
              <button className="btn btn-primary" type="submit" disabled={signingIn}>
                {signingIn ? "Signing in…" : "Sign in"}
              </button>
              <button
                className="btn"
                type="button"
                disabled={sendingReset}
                onClick={() => void onForgotPassword()}
              >
                {sendingReset ? "Sending…" : "Forgot password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return <Dashboard session={session!} />;
}

function SetPasswordForm({
  email,
  onDone,
}: {
  email: string;
  onDone: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      if (window.location.hash || window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      onDone();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>
          Set <span style={{ color: "var(--accent)" }}>password</span>
        </h1>
        <p className="muted" style={{ marginTop: 0 }}>
          Choose a password for <strong>{email}</strong> so you can sign in
          without a recovery link next time.
        </p>
        <form onSubmit={onSubmit}>
          <div className="field">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div className="field">
            <label htmlFor="confirm-password">Confirm password</label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </div>
          {error && <p className="error">{error}</p>}
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save password"}
          </button>
        </form>
      </div>
    </div>
  );
}
